import Button from 'components/Button';
import {forwardRef, useCallback} from 'react';
import {FieldError} from 'react-hook-form';
import {twMerge} from 'tailwind-merge';
import {clamp, stripLetters} from 'utils/number';

import {InputProps} from '../../types';

export type InputIncrementalBaseProps = {
    defaultValue?: number;
    error?: FieldError;
    max?: number;
    min?: number;
    name?: string;
    onDecrement?: () => void;
    onIncrement?: () => void;
} & Omit<InputProps, 'defaultValue' | 'name'>;

export const useIncrementalSanitizer = (min: number, max: number) =>
    useCallback(
        (value: string) => {
            if (value === '-' || value === '') return value;

            return String(clamp({max, min})(Number(stripLetters(value))));
        },
        [max, min],
    );

const InputIncrementalBase = forwardRef<
    HTMLInputElement,
    InputIncrementalBaseProps
>(
    (
        {
            disabled,
            error,
            id,
            name,
            onDecrement,
            onIncrement,
            required,
            ...rest
        },
        ref,
    ) => (
        <div
            className={twMerge(
                'input-wrapper flex h-10 items-end flex-1',
                !!error && 'input-invalid',
                disabled && 'disabled',
            )}
        >
            <input
                className="min-w-0 flex-1 border-none"
                disabled={disabled}
                id={id || name}
                name={name}
                ref={ref}
                required={!!required}
                type="text"
                {...rest}
            />
            <Button
                aria-label="minus"
                disabled={disabled}
                icon="minus"
                kind="tertiary"
                onClick={onDecrement}
            />
            <Button
                aria-label="plus"
                disabled={disabled}
                icon="plus"
                kind="tertiary"
                onClick={onIncrement}
            />
        </div>
    ),
);

InputIncrementalBase.displayName = 'InputIncrementalBase';

export default InputIncrementalBase;
